import { useEffect, useState } from 'react';
import styled from 'styled-components';

import FormTitle from 'components/PageLayout/FormTitle';
import { Button } from 'components/curo-components/common/Button';
import ConfirmationModal from 'components/curo-components/common/ConfirmationModal';
import { GoogleAnalyticsEventType } from 'enums/GoogleAnalyticsEventType';
import { CuroApplicationVariables } from 'enums/curo/Variables';
import { getApplicationSelector } from 'handlers/curo-handlers/selectors';
import { useAppSelector } from 'hooks/reduxHooks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import media from 'styles/media.module.scss';
import { updatePageDisplayedTechnical } from 'thunks';

const StyledWrapper = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 36px;

  @media (max-width: ${media.maxTablet}) {
    flex-direction: column;
  }
`;

interface IConnectBankAccountForFundingStepProps {
  onStepComplete: () => void;
  onPlaid: () => void;
}

const ConnectBankAccountForFundingStep = ({
  onStepComplete,
  onPlaid,
}: IConnectBankAccountForFundingStepProps) => {
  const application = useAppSelector(getApplicationSelector)!;
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const trackEvent = useGoogleAnalytics();

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    trackEvent(GoogleAnalyticsEventType.ApplyProcessBankingCollection);
  }, []);

  const updatePageDisplayed = async () => {
    await dispatchWithUnwrap(
      updatePageDisplayedTechnical({
        applicationId: application?.id,
        pageVariableToUpdate:
          CuroApplicationVariables.ConnectBankAccountForFundingInteractionTechnical,
      }),
    );
  };

  const handleOpenConfirmModal = () => {
    updatePageDisplayed();
    setIsModalOpen(true);
  };

  const handleDeclineBankAccount = async () => {
    setIsModalOpen(false);
    onStepComplete();
  };

  const handleAcceptBankAccount = async () => {
    await updatePageDisplayed();
    onPlaid();
    setIsModalOpen(false);
  };

  return (
    <>
      <div>
        <FormTitle
          title="Connect to bank account for funding"
          subTitle={
            <div>
              For verification of your income and bank account and to deposit your loan proceeds, we
              require connecting your bank account with Plaid. Please add the bank account which you
              deposit your paycheck.
              <br />
              <br />
              This process usually can be completed within minutes.
            </div>
          }
        />
        <StyledWrapper>
          <Button onClick={handleAcceptBankAccount} kind="primary" size="form">
            Connect to Primary Bank Account
          </Button>
          <Button onClick={handleOpenConfirmModal} kind="secondary" size="form">
            I do not want to connect my bank account
          </Button>
        </StyledWrapper>
      </div>
      {isModalOpen && (
        <ConfirmationModal
          title="Are you sure?"
          description="To expedite processing your loan offer and the deposit of your loan proceeds, we recommend connecting your primary bank account."
          confirmButtonTitle="Connect my bank account"
          declineButtonTitle="Do not connect"
          handleAccept={handleAcceptBankAccount}
          handleDecline={handleDeclineBankAccount}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

export default ConnectBankAccountForFundingStep;
